import { EyeOutlined } from '@ant-design/icons';
import React, { useState, useEffect } from 'react';
import { IQuestionOption } from '../../../types';

import { ImagePreviewModal } from '../ImagePreviewModal';
import { useCurrency } from '@/features/product-configurator/hooks/useCurrency';

interface MultipleChoiceCardProps {
  options: IQuestionOption[];
  selectedOptions?: IQuestionOption[];
  onSelect: (options: IQuestionOption[]) => void;
  isDisabled?: boolean;
  isProcessing?: boolean;
}

export const MultipleChoiceCard: React.FC<MultipleChoiceCardProps> = ({
  options,
  selectedOptions = [],
  onSelect,
  isDisabled = false,
}) => {
  const { formatPrice } = useCurrency();
  const [localSelectedIds, setLocalSelectedIds] = useState<IQuestionOption[]>(selectedOptions);
  const [preview, setPreview] = useState<{ src: string; alt: string } | null>(null);

  // Sync localSelectedIds with selectedOptions when it changes (e.g., due to display conditions)
  // and filter to only include options that exist in the current options list
  useEffect(() => {
    const optionIds = new Set(options.map(opt => opt.optionId));
    const filteredSelected = selectedOptions.filter(opt => optionIds.has(opt.optionId));
    setLocalSelectedIds(filteredSelected);
  }, [selectedOptions, options]);

  const isNoneOption = (opt: IQuestionOption) =>
    opt.responseValue?.toLowerCase() === 'none' ||
    opt.optionText?.toLowerCase() === 'none';

  const toggleSelection = (option: IQuestionOption) => {
    const isCurrentlySelected = localSelectedIds.some(
      (selected) => selected.optionId === option.optionId,
    );

    if (isCurrentlySelected) {
      setLocalSelectedIds(localSelectedIds.filter((s) => s.optionId !== option.optionId));
      return;
    }

    if (isNoneOption(option)) {
      setLocalSelectedIds([option]);
      return;
    }

    setLocalSelectedIds([
      ...localSelectedIds.filter((s) => !isNoneOption(s)),
      option,
    ]);
  };

  const handleContinue = () => {
    if (localSelectedIds.length > 0) {
      onSelect(localSelectedIds);
    }
  };

  const isSelected = (optionId: string) => localSelectedIds.some(
    (selected) => selected.optionId === optionId
  );

  const hasSelection = localSelectedIds.length > 0;

  return (
    <div className="w-full mb-6 max-w-[90%]">
      <div className="w-full" style={{ opacity: isDisabled ? 0.4 : 1 }}>
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(250px,1fr))]">
          {options.map((option) => {
            const selected = isSelected(option.optionId);
            return (
              <button
                key={option.optionId}
                className={`rounded-[10px] border p-3 transition-all ${selected
                  ? 'border-primary-500 bg-primary-50'
                  : 'bg-white border-neutral-400 hover:border-primary-300'
                  }`}
                onClick={() => toggleSelection(option)}
              >
                {/* Image with preview overlay */}
                {option.thumbnail && (
                  <div className="relative w-full rounded-lg overflow-hidden mb-2 group">
                    <img
                      src={option.thumbnail}
                      alt={option.optionText}
                      className="w-full h-[90px] object-contain"
                    />
                    <button
                      type="button"
                      className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={e => {
                        e.stopPropagation();
                        setPreview({ src: option.thumbnail!, alt: option.optionText });
                      }}
                      title="Preview image"
                    >
                      <EyeOutlined style={{ color: 'white', fontSize: 20 }} />
                    </button>
                  </div>
                )}

                <span
                  className={`text-[16px] text-center font-semibold block ${selected ? 'text-primary-500' : 'text-text-200'
                    }`}
                >
                  {option.optionText}
                </span>

                {option?.description && typeof option.description === 'string' && (
                  <span className="text-[12px] text-center text-text-secondary block mt-1">
                    {option.description}
                  </span>
                )}

                {option.priceModifier > 0 && (
                  <span className="text-[14px] font-bold text-primary-500 text-center block mt-1">
                    +{formatPrice(option.priceModifier)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className='flex flex-row justify-center'>
          <button
            className={`rounded-[10px] w-full max-w-[400px] py-3 px-7 mt-4 transition-all ${hasSelection ? 'bg-primary-500 hover:bg-primary-600' : 'bg-neutral-300'
              }`}
            onClick={handleContinue}
            disabled={!hasSelection}
          >
            <span
              className={`text-[16px] text-center font-semibold ${hasSelection ? 'text-white' : 'text-neutral-500'
                }`}
            >
              Continue ({localSelectedIds.length} selected)
            </span>
          </button>
        </div>
      </div>

      {preview && (
        <ImagePreviewModal
          src={preview.src}
          alt={preview.alt}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};
